<template>
  <div class="channel-details-card p-2 mb-2 match-event-slot" :class="{ 'is-editing': editing, 'is-empty': !hasData && !editing, 'is-inactive': isInactive }">
    <!-- Collapsed / compact one-row view -->
    <div v-if="!editing" class="d-flex align-items-center flex-wrap event-summary-row" style="gap: 0.6rem;">
      <template v-if="hasData">
        <span class="app-label">{{ currentApplication }}</span>
        <span v-if="linkedLanguages.length === 1" class="lang-badge">{{ linkedLanguages[0] }}</span>
        <span v-else-if="linkedLanguages.length > 1" class="lang-badge lang-badge--mixed" :title="'Linked channels use different languages: ' + linkedLanguages.join(', ')">Mixed</span>
        <span class="text-truncate" style="max-width:220px;" :title="eventLabel(matchEvent.eventId, currentApplication)">{{ eventLabel(matchEvent.eventId, currentApplication) }}</span>
        <span class="text-muted">&middot;</span>
        <span class="text-truncate time-badge" :title="'Setup ' + formatPadSetup(matchEvent.eventPadSetup) + ' before match start'">
          setup {{ formatPadSetup(matchEvent.eventPadSetup) }} before
        </span>
        <span v-if="matchEvent.locked" class="on-air-badge" title="The event shows this match now - nothing can be changed until the match ends (channel order: Live Events)">on</span>

        <!-- Channels carrying this event, inline in the same row - click it to open the checkbox
             editor underneath. Names only: a channel's language is spelled out only when the
             linked channels disagree (the language badge at the start of the row already covers
             the usual all-one-language case). -->
        <span v-if="channelOptions.length" role="button" class="channels-inline"
              :title="linkedChannelSummary || 'No channels have been linked, please select them'"
              @click="channelsExpanded = !channelsExpanded">
          <span v-if="linkedChannelList.length" class="channels-inline-list">{{ linkedChannelSummary }}</span>
          <span v-else class="channels-inline-empty">No channels linked</span>
          <span v-if="channelsDirty" class="unsaved-dot" title="Unsaved channel changes">&#9679;</span>
          <span class="chevron">{{ channelsExpanded ? '▾' : '▸' }}</span>
        </span>

        <label class="ms-auto d-flex align-items-center mb-0 active-toggle" style="gap:0.25rem;">
          <input type="checkbox" :checked="matchEvent.active" :disabled="saving || isReadOnly" @change="toggleActive($event.target.checked)">
          <span class="small">Active</span>
        </label>
        <a v-if="!isReadOnly" role="button" class="procricket-btn--reverse btn-xs" @click="startEdit">Edit</a>
      </template>

      <template v-else>
        <span class="text-muted fst-italic">Not assigned</span>
        <a v-if="!isReadOnly" role="button" class="procricket-btn--reverse btn-xs ms-auto" @click="startEdit">Assign</a>
      </template>

      <span v-if="errorMessage" class="error-message small w-100">{{ errorMessage }}</span>

      <!-- Saved assignment whose event is also held by another overlapping match (or by
           another league). The picker already disables busy events, but that only prevents new
           conflicts - a row saved before the check existed (or saved from a stale page) would
           otherwise sit here looking fine. -->
      <div v-if="hasData && matchEvent.active && conflictInfo" class="mixed-language-warning small w-100">
        &#9888; Event #{{ matchEvent.eventId }} conflicts with {{ conflictInfo.label }} - both overlap in time and can't use the same event. Pick a different event for one of them.
      </div>
      <div v-else-if="hasData && matchEvent.active && savedPadOverlap" class="mixed-language-warning small w-100">
        &#9888; Setup of {{ formatPadSetup(matchEvent.eventPadSetup) }} starts before {{ savedPadOverlap.label }} - at most {{ formatPadSetup(savedPadOverlap.maxPad) }} fits. Lower the setup time.
      </div>

      <!-- Which of this match's own already-configured channels (stream_1/stream_1b/... -
           set up on the Matches page, not the global channel list) carry this event. Multi-
           select is intentional - one event can be carried by more than one channel. Only
           shown once the event assignment itself is persisted (hasData), since the link
           needs a real matchEventId to attach to. The summary lives inline in the row above
           (.channels-inline); clicking it opens this checkbox editor underneath. -->
      <transition name="expand">
        <div v-if="hasData && channelOptions.length" v-show="channelsExpanded" class="channel-links-panel w-100">
          <div class="channel-links-section mt-1">
            <div class="channel-links-list">
              <label v-for="ch in channelOptions" :key="ch.calChannelId" class="channel-check-row"
                     :class="{ 'is-disabled': isChannelDisabled(ch.calChannelId) }">
                <input type="checkbox" :checked="draftChannelIds.includes(ch.calChannelId)"
                       :disabled="channelSaving || isReadOnly || isChannelDisabled(ch.calChannelId)"
                       :title="isChannelDisabled(ch.calChannelId) ? 'Already carrying another event for this application' : ''"
                       @change="onChannelToggle(ch.calChannelId, $event.target.checked)">
                <span v-if="channelTitle(ch)" class="channel-check-detail text-truncate">{{ channelTitle(ch) }}</span>
                <span v-if="ch.language" class="lang-badge">{{ ch.language }}</span>
                <span class="channel-check-name text-truncate" :class="{ 'channel-check-name--only': !channelTitle(ch) }">{{ ch.channelName }}</span>
                <span v-if="isChannelDisabled(ch.calChannelId)" class="channel-check-claimed text-truncate">
                  already used by another event
                </span>
              </label>
            </div>
            <!-- Save sits on the right on desktop and drops below the list on mobile (see
                 .channel-save-bar in style.css). Only shown once something actually changed. -->
            <div v-if="channelsDirty && !isReadOnly" class="channel-save-bar mt-2">
              <span class="small text-muted">Unsaved channel changes</span>
              <a role="button" class="procricket-btn--reverse btn-xs" :class="{ disabled: channelSaving }" @click="discardChannelDraft">Discard</a>
              <a role="button" class="procricket-btn btn-xs" :class="{ disabled: channelSaving }" @click="saveChannels">
                {{ channelSaving ? 'Saving...' : 'Save' }}
              </a>
            </div>
            <div v-if="channelSavedVisible" class="channel-saved-msg mt-2">Changes saved</div>
          </div>
        </div>
      </transition>
      <div v-if="hasData && !channelOptions.length" class="small text-muted fst-italic w-100 mt-1">
        No channels configured for this match yet - set them up on the Matches page first.
      </div>
      <div v-if="linkedLanguages.length > 1" class="mixed-language-warning small w-100 mt-1">
        &#9888; Linked channels use different languages: {{ linkedLanguages.join(', ') }}
      </div>
      <div v-if="channelErrorMessage" class="error-message small w-100 mt-1">{{ channelErrorMessage }}</div>
    </div>

    <!-- Edit form - two layouts, switched purely by width (see the media query below),
         not two separate features: the original single-row layout is what desktop keeps
         using (Martin: "that was good enough for desktop"), and the one-field-per-line
         stack is mobile-only, where the old row wasn't practical to edit. Both bind the
         same form fields, so whichever is visible at a given width is just a different
         view onto the same in-progress edit. -->
    <div v-else>
      <div class="edit-form-desktop d-flex align-items-center flex-nowrap" style="gap: 0.5rem; overflow-x: auto;">
        <div class="d-flex align-items-center text-nowrap" style="gap: 0.3rem;">
          <label class="small mb-0">Application</label>
          <select class="form-select form-select-sm" style="width: 130px;" v-model="formApplication" @change="onApplicationChange" title="Application">
            <option :value="undefined" disabled>Select...</option>
            <option v-for="app in applicationList" :key="app" :value="app">{{ app }}</option>
          </select>
        </div>

        <div class="d-flex align-items-center text-nowrap" style="gap: 0.3rem; flex: 1 1 0; min-width: 0;">
          <label class="small mb-0">Event</label>
          <select class="form-select form-select-sm" style="flex: 1 1 0; min-width: 0;" v-model.number="formEventId" :disabled="!formApplication" @change="onEventChange" title="Event">
            <option :value="undefined" disabled>{{ eventSelectPlaceholder }}</option>
            <option v-for="ev in eventOptions" :key="ev.eventId" :value="ev.eventId" :disabled="!!takenInfo(ev.eventId) && ev.eventId !== savedEventId" :class="{ 'option-taken': !!takenInfo(ev.eventId) }">{{ eventOptionLabel(ev.eventId) }}</option>
          </select>
        </div>

        <div class="d-flex align-items-center text-nowrap" style="gap: 0.3rem;">
          <label class="small mb-0" title="Minutes before match start to rewrite the event and create its channel links">Setup lead (min)</label>
          <input type="number" min="0" :max="effectiveMaxPad" class="form-control form-control-sm" style="width: 80px;" v-model.number="formPadSetup" title="Minutes before match start to rewrite the event and create its channel links">
          <span v-if="padLimit" class="small text-muted" :class="{ 'text-danger': formPadSetup > effectiveMaxPad }" :title="padLimit.label">max {{ formatPadSetup(padLimit.maxPad) }}</span>
        </div>

        <span class="ms-auto d-flex align-items-center" style="gap: 0.4rem;">
          <a role="button" class="procricket-btn--reverse btn-xs text-nowrap" :class="{ disabled: saving }" @click="cancelEdit">Cancel</a>
          <a role="button" class="procricket-btn btn-xs text-nowrap" :class="{ disabled: !canSave || saving }" @click="save">
            {{ saving ? 'Saving...' : 'Save' }}
          </a>
        </span>
      </div>

      <div class="edit-form-mobile match-event-edit-form">
        <div class="edit-field">
          <label class="small mb-0">Application</label>
          <select class="form-select form-select-sm" v-model="formApplication" @change="onApplicationChange" title="Application">
            <option :value="undefined" disabled>Select...</option>
            <option v-for="app in applicationList" :key="app" :value="app">{{ app }}</option>
          </select>
        </div>

        <div class="edit-field">
          <label class="small mb-0">Event</label>
          <select class="form-select form-select-sm" v-model.number="formEventId" :disabled="!formApplication" @change="onEventChange" title="Event">
            <option :value="undefined" disabled>{{ eventSelectPlaceholder }}</option>
            <option v-for="ev in eventOptions" :key="ev.eventId" :value="ev.eventId" :disabled="!!takenInfo(ev.eventId) && ev.eventId !== savedEventId" :class="{ 'option-taken': !!takenInfo(ev.eventId) }">{{ eventOptionLabel(ev.eventId) }}</option>
          </select>
        </div>

        <div class="edit-field">
          <label class="small mb-0" title="Minutes before match start to rewrite the event and create its channel links">Setup lead (min)</label>
          <input type="number" min="0" :max="effectiveMaxPad" class="form-control form-control-sm" v-model.number="formPadSetup" title="Minutes before match start to rewrite the event and create its channel links">
          <span v-if="padLimit" class="small text-muted" :class="{ 'text-danger': formPadSetup > effectiveMaxPad }">max {{ formatPadSetup(padLimit.maxPad) }} - {{ padLimit.label }}</span>
        </div>

        <div class="d-flex mt-2" style="gap: 0.5rem;">
          <a role="button" class="procricket-btn--reverse btn-xs flex-fill text-center" :class="{ disabled: saving }" @click="cancelEdit">Cancel</a>
          <a role="button" class="procricket-btn btn-xs flex-fill text-center" :class="{ disabled: !canSave || saving }" @click="save">
            {{ saving ? 'Saving...' : 'Save' }}
          </a>
        </div>
      </div>

      <div v-if="errorMessage" class="error-message small mt-1">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'MatchEventSlotEditor',
  props: {
    // This match's current match_events row for this slot, or null/undefined if it hasn't
    // been assigned yet. There's no fixed application per slot - which application a saved
    // row belongs to is resolved through the chosen event's own application field
    // (eventsById), same as the parent does.
    matchEvent: { type: Object, default: null },
    // Every known application - the free-form list this component picks from. Not one row
    // per application: a match can have several of these, and one application can appear
    // more than once here as long as the records differ (in practice, by language).
    applicationList: { type: Array, default: () => [] },
    // { [application]: Event[] } - the event picker filters to whichever application is
    // currently chosen in this row's own form.
    eventsByApplication: { type: Object, default: () => ({}) },
    // { [eventId]: Event } - resolves a persisted row's bare eventId back to its event's
    // own content/application for display.
    eventsById: { type: Object, default: () => ({}) },
    // { [eventId]: { calMatchId, label } } - events already in use by another match during
    // this match (see the parent's takenEventWindowsForMatch). Never includes this slot's own
    // assignment. The picker still lists them, disabled, with who holds them.
    takenEventWindows: { type: Object, default: () => ({}) },
    // { [eventId]: { maxPad, label } } - for events free during this match, the most setup
    // time that fits after the previous match on that event ends (see the parent's
    // eventPadLimitsForMatch). Absent = only maxPadSetupMinutes applies.
    padLimits: { type: Object, default: () => ({}) },
    saving: { type: Boolean, default: false },
    // Past match: the assignment and its linked channels are shown, nothing can be changed.
    readOnly: { type: Boolean, default: false },
    // Set by the parent right after this assignment was saved with no channels yet: opens the
    // channel checklist on its own so the next step (linking channels) is obvious.
    autoOpenChannels: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
    // From AdminService.getCalendarStaticData() (CalMatches.vue) - the hard ceiling on
    // padSetup, also enforced server-side (see CalLeagueService.upsertMatchEvent), no longer
    // hardcoded here. Default of 720 (12h) matches the old literal, in case the parent
    // hasn't loaded the static data yet.
    maxPadSetupMinutes: { type: Number, default: 720 },
    // This match's own active CalChannel rows (stream_1/stream_1b/... from the Matches page),
    // NOT the global channel list - see docs/be-tasks/cal-event-channel-link.md. Each entry
    // is expected to carry at least calChannelId/channelName, plus encoder/encStreamName for
    // the checkbox's tooltip.
    channelOptions: { type: Array, default: () => [] },
    // calChannelId[] currently linked (active) to this row's matchEvent.
    linkedChannelIds: { type: Array, default: () => [] },
    // calChannelId[] already linked (active) to some OTHER match_events row on this same
    // match that shares this row's application - MatchEvents.vue's
    // channelsClaimedElsewhere, scoped same-match/same-application so the same physical
    // feed is never told to carry two events for the one application at once. A channel
    // this row already has linked itself is never in here (see channelsClaimedElsewhere's
    // own excludeMatchEventId) - only checked boxes on OTHER rows disable this one.
    disabledChannelIds: { type: Array, default: () => [] },
    channelSaving: { type: Boolean, default: false },
    channelErrorMessage: { type: String, default: '' }
  },
  emits: ['save', 'save-channels', 'remove'],
  data() {
    return {
      editing: !this.hasDataFor(this.matchEvent) && !this.readOnly && !this.matchEvent?.locked,
      formApplication: undefined,
      formEventId: undefined,
      // Minutes before match start at which the (future) setup step rewrites the event and
      // creates its channel links. Initial value is the configured ceiling itself (the
      // maxPadSetupMinutes prop), no longer a hardcoded literal.
      formPadSetup: this.maxPadSetupMinutes,
      // Whether the Channels checkbox editor (below the collapsed summary row) is open -
      // always starts closed, same as the match's own Events/Sources sections.
      channelsExpanded: false,
      previousLinked: [...this.linkedChannelIds],
      // Checked channels while editing, not yet saved - checkbox clicks only change this; Save sends it
      // as one request after a confirm. Kept in sync with linkedChannelIds whenever there's no pending edit.
      draftChannelIds: [...this.linkedChannelIds],
      channelSavedVisible: false,
      channelSavedTimer: null
    }
  },
  beforeUnmount() {
    clearTimeout(this.channelSavedTimer);
  },
  mounted() {
    if (this.autoOpenChannels) this.openChannelsPanel();
  },
  watch: {
    autoOpenChannels(now) {
      if (now) this.openChannelsPanel();
    },
    // The match finished (or the event went on) while this was open: close the form, nothing can be changed any more.
    isReadOnly(now) {
      if (now) this.editing = false;
    },
    // Re-sync the draft from the saved links (e.g. right after a successful Save, or when another
    // refetch changes them) - but never clobber edits the user is in the middle of.
    linkedKey() {
      if (!this.channelsDirtyAgainst(this.previousLinked)) this.draftChannelIds = [...this.linkedChannelIds];
      this.previousLinked = [...this.linkedChannelIds];
    },
    // The parent owns the save (saving flips true -> false once the upsert AND the list refetch
    // are done, with errorMessage set on failure). A save that finished cleanly on a row that
    // now exists returns to the normal read-only view; a failed one stays open with its error.
    // Channel save finished cleanly -> "Changes saved" for 5 seconds (no browser popup).
    channelSaving(now, before) {
      if (!before || now) return;
      this.$nextTick(() => {
        if (this.channelErrorMessage) return;
        this.channelSavedVisible = true;
        clearTimeout(this.channelSavedTimer);
        this.channelSavedTimer = setTimeout(() => { this.channelSavedVisible = false; }, 5000);
      });
    },
    saving(now, before) {
      if (before && !now && this.editing && !this.errorMessage && this.hasData) {
        this.editing = false;
      }
    }
  },
  computed: {
    // Past match, or the event is on (the backend's locked: the event window is open and this assignment was set up
    // before it) - the backend refuses changes to the assignment and its channels until the match ends.
    isReadOnly() {
      return this.readOnly || !!this.matchEvent?.locked;
    },
    // A saved assignment that is switched off: the card is greyed and only its Active checkbox works.
    isInactive() {
      return this.hasData && this.matchEvent.active === false;
    },
    linkedKey() {
      return [...this.linkedChannelIds].sort((a, b) => a - b).join(',');
    },
    channelsDirty() {
      return this.channelsDirtyAgainst(this.linkedChannelIds);
    },
    hasData() {
      return this.hasDataFor(this.matchEvent);
    },
    // Not stored on match_events itself - read back off whichever event this row points at.
    currentApplication() {
      return this.hasData ? (this.eventsById[this.matchEvent.eventId]?.application || '—') : undefined;
    },
    // Every event of the chosen application - busy ones are shown disabled with the reason
    // (see eventOptionLabel), so the admin can see who holds an event instead of it vanishing.
    eventOptions() {
      return this.eventsByApplication[this.formApplication] || [];
    },
    freeEventCount() {
      return this.eventOptions.filter(ev => !this.takenInfo(ev.eventId)).length;
    },
    // This slot's own saved event - kept selectable even if it's now flagged as busy.
    savedEventId() {
      return this.hasData ? this.matchEvent.eventId : undefined;
    },
    // Limit for the event currently picked in the form, if the previous match caps it.
    padLimit() {
      return this.formEventId ? this.padLimits[this.formEventId] : undefined;
    },
    effectiveMaxPad() {
      return this.padLimit ? Math.min(this.maxPadSetupMinutes, Math.max(this.padLimit.maxPad, 0)) : this.maxPadSetupMinutes;
    },
    // A saved row whose setup time reaches back into the previous match on the same event.
    savedPadOverlap() {
      if (!this.hasData) return undefined;
      const limit = this.padLimits[this.matchEvent.eventId];
      return limit && this.matchEvent.eventPadSetup > limit.maxPad ? limit : undefined;
    },
    // Non-undefined when this saved row's own event is also claimed by another overlapping
    // match. takenEventWindows never includes this match's own assignments, so a hit here is
    // always a genuine conflict with someone else.
    conflictInfo() {
      return this.hasData ? this.takenInfo(this.matchEvent.eventId) : undefined;
    },
    eventSelectPlaceholder() {
      if (!this.formApplication) return 'Pick an application first';
      return this.freeEventCount ? 'Select...' : 'No free events for this window';
    },
    canSave() {
      return !!(this.formApplication && this.formEventId &&
          this.formPadSetup !== undefined && this.formPadSetup !== null && this.formPadSetup >= 0 && this.formPadSetup <= this.effectiveMaxPad);
    },
    // Languages actually carried by this row's currently-linked channels - the closest
    // thing this event has to "its language" now that match_events no longer stores one of
    // its own (see docs/be-tasks/cal-event-channel-link.md). Empty until at least one
    // channel is linked; more than one distinct value means the linked channels disagree,
    // which the template flags instead of silently picking one.
    // The channelOptions rows currently linked (active) to this row's matchEvent, in the
    // same order as channelOptions - what the inline channels summary in the row lists.
    linkedChannelList() {
      return this.channelOptions.filter(ch => this.isChannelLinked(ch.calChannelId));
    },
    // "stream_1, stream_1b" for the inline summary; a channel's language is appended only when
    // the linked channels disagree (see linkedLanguages).
    linkedChannelSummary() {
      const mixed = this.linkedLanguages.length > 1;
      return this.linkedChannelList
          .map(ch => (mixed && ch.language ? `${ch.channelName} (${ch.language})` : ch.channelName))
          .join(', ');
    },
    linkedLanguages() {
      const langs = this.channelOptions
          .filter(ch => this.isChannelLinked(ch.calChannelId) && ch.language)
          .map(ch => ch.language);
      return [...new Set(langs)];
    }
  },
  methods: {
    hasDataFor(me) {
      return !!(me && me.eventId);
    },
    // "#12 - Order Number 3" - identifies which recurring event slot this is by its id and
    // order number alone, not by its current team matchup or league (that content rotates
    // independently of which physical slot this is, so showing it here was actively
    // misleading).
    eventLabel(eventId, application) {
      const pool = this.eventsByApplication[application] || [];
      const ev = pool.find(e => e.eventId === eventId) || this.eventsById[eventId];
      if (!ev) return `Event #${eventId}`;
      const order = ev.orderNumber !== undefined && ev.orderNumber !== null ? ` - Order Number ${ev.orderNumber}` : '';
      return `#${eventId}${order}`;
    },
    // "#12 - Order Number 3 - busy: Match #482, 20 Sep 14:00-17:00" for a busy event.
    eventOptionLabel(eventId) {
      const base = this.eventLabel(eventId, this.formApplication);
      const taken = this.takenInfo(eventId);
      return taken ? `${base} - busy: ${taken.label}` : base;
    },
    // Picking an event fills in the largest setup time that fits (the previous match on that
    // event may cap it below maxPadSetupMinutes). The admin can lower it before saving.
    onEventChange() {
      // After v-model has written formEventId, so padLimit/effectiveMaxPad see the new pick.
      this.$nextTick(() => {
        this.formPadSetup = this.effectiveMaxPad;
      });
    },
    // Undefined unless this event is already in use by another match during this one
    // (see the takenEventWindows prop above).
    takenInfo(eventId) {
      return this.takenEventWindows[eventId];
    },
    isChannelLinked(calChannelId) {
      return this.linkedChannelIds.includes(calChannelId);
    },
    // No BE check for this - MatchEvents.vue already has every match_events/
    // cal_event_channel row for the league in memory, so "is this channel already carrying
    // another event of the same application on this match" is resolved entirely client-side
    // and enforced by just not offering the box, rather than round-tripping a save that
    // would get rejected.
    isChannelDisabled(calChannelId) {
      return this.disabledChannelIds.includes(calChannelId);
    },
    // "H264 · xa_1234" - encoder + Xaccel stream name, shown first and prominent next to the checkbox
    // (see channel-check-detail below), with the channel name small after it, so the admin can
    // tell channels of the same slot family apart without leaving this row.
    channelTitle(ch) {
      const parts = [ch.encoder, ch.encStreamName].filter(Boolean);
      return parts.length ? parts.join(' \u00b7 ') : '';
    },
    channelsDirtyAgainst(saved) {
      const a = [...this.draftChannelIds].sort((x, y) => x - y).join(',');
      const b = [...saved].sort((x, y) => x - y).join(',');
      return a !== b;
    },
    onChannelToggle(calChannelId, checked) {
      const without = this.draftChannelIds.filter(id => id !== calChannelId);
      this.draftChannelIds = checked ? [...without, calChannelId] : without;
    },
    discardChannelDraft() {
      this.draftChannelIds = [...this.linkedChannelIds];
    },
    // Hands the complete desired set to the parent as ONE request.
    openChannelsPanel() {
      if (this.isReadOnly || !this.hasData || !this.channelOptions.length) return;
      this.channelsExpanded = true;
      this.$nextTick(() => {
        if (this.$el && this.$el.scrollIntoView) this.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
    saveChannels() {
      if (this.channelSaving || !this.channelsDirty || this.isReadOnly) return;
      this.channelSavedVisible = false;
      this.$emit('save-channels', { calChannelIds: [...this.draftChannelIds] });
    },
    formatPadSetup(minutes) {
      if (minutes === undefined || minutes === null) return '—';
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      if (h === 0) return `${m}m`;
      return m === 0 ? `${h}h` : `${h}h ${m}m`;
    },
    startEdit() {
      if (this.isReadOnly) return;
      // currentApplication is undefined for an empty slot, or '—' if the saved eventId
      // doesn't resolve to a known event (stale data) - either way, force a real pick.
      this.formApplication = this.currentApplication && this.currentApplication !== '—' ? this.currentApplication : undefined;
      this.formEventId = this.matchEvent?.eventId;
      this.formPadSetup = this.matchEvent?.eventPadSetup ?? this.maxPadSetupMinutes;
      this.editing = true;
    },
    cancelEdit() {
      // A still-empty slot (never saved - see hasData) never had anything to lose by
      // cancelling. Rather than collapsing to a "Not assigned" placeholder that then just
      // sits there with no way back except re-opening it, drop it from the visible list
      // outright (see MatchEvents.vue's onMatchEventRemove).
      if (!this.hasData) {
        this.$emit('remove', { matchEventId: undefined });
        return;
      }
      this.editing = false;
    },
    // Switching application invalidates whatever event was picked against the old one's list.
    onApplicationChange() {
      this.formEventId = undefined;
    },
    // Timing/event choice untouched by an active/inactive flip - carry them straight
    // through unchanged, same as CalChannelSlotEditor's toggleActive.
    toggleActive(checked) {
      if (this.isReadOnly) return;
      this.$emit('save', {
        matchEventId: this.matchEvent.matchEventId,
        eventId: this.matchEvent.eventId,
        padSetup: this.matchEvent.eventPadSetup,
        active: checked
      });
    },
    save() {
      if (!this.canSave || this.saving || this.isReadOnly) return;
      this.$emit('save', {
        // Present when editing an already-persisted row - undefined for a fresh assignment,
        // which is fine: the parent falls back to matching on (calMatchId, eventId) for
        // those (UNIQUE(cal_match_id, event_id) on the BE).
        matchEventId: this.matchEvent?.matchEventId,
        eventId: this.formEventId,
        padSetup: this.formPadSetup,
        active: true
      });
    }
  }
})
</script>

<style src="@/assets/style.css"></style>
<style scoped>
/* Busy events stay listed but greyed-red, with who holds them in the option text. */
.option-taken {
  color: #dc3545;
}
</style>
